/**
 * File System Access API sink with graceful fallback.
 *
 * Lets a caller write streamed output (chunks of bytes) into either
 *   1. a directory the user picked via showDirectoryPicker(),    OR
 *   2. an in-memory Map of Uint8Array chunks that gets stitched into a
 *      Blob and downloaded via <a download> on close().
 *
 * Pattern lifted from public/js/file-transfer/receiver.js so the disk-image
 * cluster, file-transfer, and any future tool can share one tested fallback
 * chain. The original receiver still has its own copy until a follow-up
 * refactors it; nothing in this file touches receiver.
 *
 * Surface: window.__FsaSink
 *   var sink = window.__FsaSink.create(dirHandleOrNull, { fallbackLimitBytes });
 *   sink.openFile(relPath)         → Promise<FileWriter>
 *   writer.write(uint8 | ArrayBuffer | string) → Promise
 *   writer.close()                 → Promise<{ name, size, blob? }>
 *   writer.discard()               → after a failed write: no swap file, and no
 *                                    empty entry unless the name existed before
 *   sink.flushFallback()           → triggers downloads for in-memory files
 *   sink.dispose()                 → revokes URLs / drops refs
 */
(function (w) {
    'use strict';
    if (w.__FsaSink && w.__FsaSink.__fromTimbricaShared) return;

    var DEFAULT_FALLBACK_LIMIT = 500 * 1024 * 1024; // per-file ceiling in fallback mode

    function isWritableHandle(h) {
        return h && typeof h === 'object'
            && typeof h.getDirectoryHandle === 'function'
            && typeof h.getFileHandle === 'function';
    }

    // Byte length of anything createWritable() accepts. A Blob reports `size`,
    // not `byteLength`/`length` — without this branch a whole-Blob write was
    // counted as 0 bytes and close() reported size 0 for a file that landed
    // fine, which is exactly the kind of "confirmation" that confirms nothing.
    function _chunkLen(chunk) {
        if (!chunk) return 0;
        if (chunk.byteLength != null) return chunk.byteLength | 0;
        if (typeof Blob !== 'undefined' && chunk instanceof Blob) return chunk.size | 0;
        if (chunk.length != null) return chunk.length | 0;
        return 0;
    }

    function _safeName(n) {
        // Strip leading slashes; collapse repeated; reject parent traversals.
        var s = String(n || '').replace(/\\/g, '/').replace(/^\/+/, '');
        if (!s || /(^|\/)\.\.(\/|$)/.test(s)) {
            throw new Error('FsaSink: invalid path "' + n + '"');
        }
        return s;
    }

    function create(dirHandle, opts) {
        opts = opts || {};
        var limit = opts.fallbackLimitBytes != null ? opts.fallbackLimitBytes : DEFAULT_FALLBACK_LIMIT;
        var useFsa = isWritableHandle(dirHandle);
        var memoryFiles = useFsa ? null : []; // [{name, blob, size}]
        var disposed = false;

        function _ensureDir(handle, segments) {
            // Walk segments, creating each directory under handle. Returns leaf dir handle.
            var p = Promise.resolve(handle);
            segments.forEach(function (seg) {
                p = p.then(function (h) { return h.getDirectoryHandle(seg, { create: true }); });
            });
            return p;
        }

        function openFile(relPath) {
            if (disposed) return Promise.reject(new Error('FsaSink: disposed'));
            var safe = _safeName(relPath);
            var parts = safe.split('/');
            var fileName = parts.pop();

            if (useFsa) {
                var leaf = null, existed = false;
                return _ensureDir(dirHandle, parts).then(function (dh) {
                    leaf = dh;
                    // Was the name there before us? getFileHandle({create})
                    // makes an EMPTY entry at once, while the bytes go to a swap
                    // file until close(). A write that fails (disk full, device
                    // pulled) therefore left a 0-byte file named like a result —
                    // unless we know it is ours to remove (see discard()).
                    return dh.getFileHandle(fileName).then(function () { existed = true; }, function () {});
                }).then(function () {
                    return leaf.getFileHandle(fileName, { create: true });
                }).then(function (fh) {
                    return fh.createWritable({ keepExistingData: false }).catch(function (err) {
                        if (existed || typeof leaf.removeEntry !== 'function') throw err;
                        return leaf.removeEntry(fileName).catch(function () {}).then(function () { throw err; });
                    });
                }).then(function (writable) {
                    var written = 0;
                    return {
                        write: function (chunk) {
                            written += _chunkLen(chunk);
                            return writable.write(chunk);
                        },
                        abort: function () {
                            try { writable.abort(); } catch (_) {}
                            return Promise.resolve();
                        },
                        /* After a failed write: drop the swap file and, when the
                           entry did not exist before, the empty entry too. A file
                           that existed stays untouched — the swap file never
                           replaced it. */
                        discard: function () {
                            var p;
                            try { p = Promise.resolve(writable.abort()).catch(function () {}); }
                            catch (_) { p = Promise.resolve(); }
                            if (existed || typeof leaf.removeEntry !== 'function') return p;
                            return p.then(function () {
                                return leaf.removeEntry(fileName).catch(function () {});
                            });
                        },
                        close: function () {
                            return writable.close().then(function () {
                                return { name: safe, size: written, mode: 'fsa' };
                            });
                        },
                        get size() { return written; },
                        get mode() { return 'fsa'; }
                    };
                });
            }

            // In-memory fallback writer
            var chunks = [];
            var written = 0;
            return Promise.resolve({
                write: function (chunk) {
                    var piece;
                    if (chunk instanceof Uint8Array) piece = chunk;
                    // A Blob is kept AS a Blob: it is already browser-managed
                    // (spillable to disk) and Blob's own constructor stitches
                    // parts, so re-reading it into a Uint8Array here would put
                    // the whole file back on the JS heap for nothing.
                    else if (typeof Blob !== 'undefined' && chunk instanceof Blob) piece = chunk;
                    else if (chunk && chunk.buffer && chunk.byteLength != null) piece = new Uint8Array(chunk.buffer, chunk.byteOffset || 0, chunk.byteLength);
                    else if (chunk instanceof ArrayBuffer) piece = new Uint8Array(chunk);
                    else if (typeof chunk === 'string') piece = new TextEncoder().encode(chunk);
                    else throw new Error('FsaSink: unsupported chunk type');
                    written += _chunkLen(piece);
                    if (written > limit) {
                        throw new Error('FsaSink: fallback per-file limit exceeded (' + limit + ' bytes)');
                    }
                    chunks.push(piece);
                    return Promise.resolve();
                },
                abort: function () {
                    chunks.length = 0;
                    return Promise.resolve();
                },
                close: function () {
                    var blob = new Blob(chunks, { type: 'application/octet-stream' });
                    chunks.length = 0;
                    var record = { name: safe, size: written, blob: blob, mode: 'fallback' };
                    memoryFiles.push(record);
                    return Promise.resolve(record);
                },
                get size() { return written; },
                get mode() { return 'fallback'; }
            });
        }

        // Fallback delivery goes through the shared download primitive, not a
        // hand-rolled anchor. The old anchor here had its own 4 s revoke and
        // skipped __downloadFile entirely, so this shared module quietly opted
        // out of everything the primitive owns: WebView/iOS routing (a blob
        // anchor is a silent no-op there), the 120 s revoke window, the
        // readability pre-flight, the honest failure toast and the delivery
        // verdict. A shared sink must not be the one place that bypasses the
        // shared front door.
        //
        // ⚠️ "Sequential delivery is fine per se — measured 5/5 for small files"
        // stood here and is WRONG. That 5/5 came from Playwright, whose CDP
        // download override bypasses Chrome's gate. Re-measured 2026-08-09 in
        // real Chrome 151 (real profile, real keypress, no CDP): a browser
        // hands over ONE file per USER GESTURE, and this whole loop runs inside
        // one. 4 files fired → 1 landed. Spacing them out does NOT help: 350 ms
        // apart → 1/4, and 3 s apart (9 s total) → 1/4. It is a per-gesture
        // budget, not a rate limit.
        // Worse, `delivered` cannot see it: the classic <a download> channel
        // returns true whether the browser saved the file or dropped it, so the
        // verdict below is honest only for the WebView/share/FSA channels.
        // FIXED 2026-08-29: this no longer fires N downloads at all. The whole
        // batch leaves as ONE archive through __batchDeliver — the archive is
        // the lane a declined folder degrades to, and one archive is one
        // download, which is exactly the budget a gesture has. Per-file buttons
        // (the /bin-cue-to-iso shape) remain the right answer where the user
        // wants to pick, and the folder remains the right answer for a batch
        // too big for any archive.
        function flushFallback(opts2) {
            opts2 = opts2 || {};
            if (useFsa || disposed) return Promise.resolve([]);
            var pending = memoryFiles.slice();
            memoryFiles.length = 0;
            if (!pending.length) return Promise.resolve([]);

            var flat = pending.map(function (rec) {
                return { name: rec.name.split('/').pop(), blob: rec.blob, size: rec.size, path: rec.name };
            });

            // ONE archive, not N downloads. The user already declined the
            // folder, so the archive is asked for directly (skipFolder) rather
            // than nagging with the same dialog twice.
            var have = w.__batchDeliver ? Promise.resolve(w.__batchDeliver)
                : (w.__ensureBatchDeliver ? w.__ensureBatchDeliver() : Promise.reject(new Error('no-loader')));
            return have.then(function (BD) {
                return BD.deliver(flat, {
                    skipFolder: true,
                    zipName: opts2.zipName || 'files.zip',
                    onProgress: opts2.onProgress
                }).then(function (res) {
                    var ok = res.via === 'zip' || res.via === 'folder';
                    return flat.map(function (f) {
                        return { name: f.path, size: f.size, delivered: ok };
                    });
                });
            }, function () { return lastResort(); });

            function lastResort() {
            // Batch primitive absent (blocked, offline). One file is the honest
            // amount this gesture can carry; the rest go to the hold queue,
            // which offers them back on the NEXT gesture. Clicking them into the
            // void is the failure this whole path exists to avoid.
            var first = flat.shift();
            if (w.__dlHold) flat.forEach(function (f) { w.__dlHold.add(f.blob, f.name); });
            var out = flat.map(function (f) { return { name: f.path, size: f.size, delivered: null }; });
            if (typeof w.__downloadFile === 'function') {
                return Promise.resolve(w.__downloadFile(first.blob, first.name)).then(function (okDl) {
                    out.unshift({ name: first.path, size: first.size, delivered: !!okDl });
                    return out;
                }, function () {
                    out.unshift({ name: first.path, size: first.size, delivered: false });
                    return out;
                });
            }
            // Core bundle absent: attached anchor so the global interceptor can
            // still take over. Never detached.
            var url = URL.createObjectURL(first.blob);
            var a = document.createElement('a');
            a.href = url; a.download = first.name; a.rel = 'noopener';
            a.style.display = 'none';
            document.body.appendChild(a); a.click();
            setTimeout(function () {
                try { document.body.removeChild(a); } catch (_) {}
                URL.revokeObjectURL(url);
            }, opts2.revokeAfterMs || 120000);
            out.unshift({ name: first.path, size: first.size, delivered: null });
            return Promise.resolve(out);
            }
        }

        function dispose() {
            disposed = true;
            if (memoryFiles) memoryFiles.length = 0;
        }

        return {
            mode: useFsa ? 'fsa' : 'fallback',
            openFile: openFile,
            flushFallback: flushFallback,
            dispose: dispose,
            isFsa: function () { return useFsa; },
            getFallbackFiles: function () { return useFsa ? [] : memoryFiles.slice(); }
        };
    }

    function probeWriteSupport(dirHandle) {
        // Validates that the FSA dirHandle accepts createWritable() — some users grant read-only.
        if (!isWritableHandle(dirHandle)) return Promise.resolve({ ok: false, reason: 'no-fsa' });
        var probeName = '.timbrica-write-probe-' + Date.now();
        return dirHandle.getFileHandle(probeName, { create: true }).then(function (fh) {
            return fh.createWritable().then(function (w2) {
                return w2.write(new Uint8Array([1])).then(function () { return w2.close(); });
            }).then(function () {
                if (typeof dirHandle.removeEntry === 'function') {
                    return dirHandle.removeEntry(probeName).catch(function () {});
                }
            });
        }).then(function () {
            return { ok: true };
        }).catch(function (err) {
            return { ok: false, reason: (err && err.name) || 'probe-failed' };
        });
    }

    w.__FsaSink = {
        create: create,
        probeWriteSupport: probeWriteSupport,
        DEFAULT_FALLBACK_LIMIT: DEFAULT_FALLBACK_LIMIT,
        __fromTimbricaShared: true
    };
})(window);
